import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "environments/environment";

import { catchError, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { UserForm } from "app/interfaces/user-form.interface";
import { VideoForm } from "app/interfaces/video-form.interface";
import { EMPTY, Observable, ReplaySubject, Subject, of } from "rxjs";

const videosUrlApi = environment.videosUrlApi;
const videoUrlApi = environment.videoUrlApi;
const videosOwnerUrlApi = environment.videosOwnerUrlApi;
const videosCheckVideoUrlApi = environment.videosCheckVideoUrlApi;

const videosBookmarksCheckUnCheckListUrlApi =
  environment.videosBookmarksCheckUnCheckListUrlApi;

const videosBlockUnBlockListUrlApi = environment.videosBlockUnBlockListUrlApi;
const videosBookmarksListUrlApi = environment.videosBookmarksListUrlApi;
const gpsOneVideoUrlApi = environment.gpsOneVideoUrlApi;
const videoMetadataUrlApi = environment.videoMetadataUrlApi;
const videosgetPosterVideoUrlApi = environment.videosgetPosterVideoUrlApi;
const videoPosterRequestsEnabled = environment.enableVideoPosterRequests !== false;

const videoExtensionsListUrlApi = environment.videoExtensionsListUrlApi;

const videosDownloadVideoUrlApi = environment.videosDownloadVideoUrlApi;

interface VideoPosterLazyQueueItem {
  id: number;
  subject: ReplaySubject<any>;
}

@Injectable()
export class VideoService {
  public user: UserForm;
  private readonly posterLazyRequestConcurrency = 2;
  private readonly posterLazyQueue$ = new Subject<VideoPosterLazyQueueItem>();
  private readonly posterLazyRequestCache = new Map<number, Observable<any>>();

  constructor(private http: HttpClient) {
    if (!videoPosterRequestsEnabled) {
      return;
    }

    this.posterLazyQueue$
      .pipe(
        mergeMap(
          ({ id, subject }) =>
            this.http
              .request("GET", `${videosgetPosterVideoUrlApi}/` + id, {
                responseType: "json",
              })
              .pipe(
                map((resp: any) => resp),
                tap((resp: any) => subject.next(resp)),
                tap({ complete: () => subject.complete() }),
                catchError((error) => {
                  this.posterLazyRequestCache.delete(id);
                  subject.error(error);
                  return EMPTY;
                })
              ),
          this.posterLazyRequestConcurrency
        )
      )
      .subscribe();
  }

  private sortVideosByMostRecent(videos: any[] = []) {
    return [...videos].sort((a, b) => {
      const getTimestamp = (video: any) => {
        const rawValue = video?.start_time || video?.createdAt || null;
        const parsedValue = rawValue ? new Date(rawValue).getTime() : 0;
        return Number.isNaN(parsedValue) ? 0 : parsedValue;
      };

      const timestampDiff = getTimestamp(b) - getTimestamp(a);
      if (timestampDiff !== 0) {
        return timestampDiff;
      }

      return Number(b?.id_video || 0) - Number(a?.id_video || 0);
    });
  }

  getVideos(
    from: string,
    to: string,
    id_police_station?: string,
    device_id?: string
  ) {
    const body: any = { from, to, id_police_station, device_id };

    return this.http
      .request("POST", `${videosUrlApi}`, { body })
      .pipe(
        map((resp: any) => ({
          ...resp,
          data: this.sortVideosByMostRecent(resp?.data),
        }))
      );
  }

  getOwnVideos(
    from: string,
    to: string,
    id_police_station?: string,
    device_id?: string
  ) {
    const body: any = { from, to };
    return this.http
      .request("POST", `${videosOwnerUrlApi}`, { body })
      .pipe(
        map((resp: any) => ({
          ...resp,
          data: this.sortVideosByMostRecent(resp?.data),
        }))
      );
  }

  getVideoById(id: number) {
    return this.http
      .request("GET", `${videosUrlApi}/` + id, { responseType: "json" })
      .pipe(map((resp: any) => resp));
  }

  getPosterOfVideoById(id: number) {
    if (!videoPosterRequestsEnabled) {
      return of({ error: true, data: null, disabled: true });
    }

    return this.http
      .request("GET", `${videosgetPosterVideoUrlApi}/` + id, {
        responseType: "json",
      })
      .pipe(map((resp: any) => resp));
  }

  getPosterOfVideoByIdLazy(id: number) {
    if (!videoPosterRequestsEnabled) {
      return of({ error: true, data: null, disabled: true });
    }

    const cachedRequest = this.posterLazyRequestCache.get(id);
    if (cachedRequest) {
      return cachedRequest;
    }

    const subject = new ReplaySubject<any>(1);
    const request$ = subject.asObservable();

    this.posterLazyRequestCache.set(id, request$);
    this.posterLazyQueue$.next({ id, subject });

    return request$;
  }

  getVideoExtensionAvailable() {
    return this.http
      .request("GET", `${videoExtensionsListUrlApi}`, { responseType: "json" })
      .pipe(map((resp: any) => resp));
  }

  uploadVideo(id, file: File, videoModel: VideoForm): Observable<any> {
    if (!videoModel.user_id) {
      videoModel.user_id = id;
    }
    const formData = new FormData();
    formData.append("video_uploaded", file, file.name);
    formData.append("user_id", String(videoModel.user_id));
    formData.append("title", videoModel.title);
    formData.append("start_time", videoModel.start_time);
    formData.append("id_device", String(videoModel.id_device));
    return this.http.post(environment.videoUrlApi, formData);
  }

  downloadVideo(id) {
    console.log(id);
    return this.http
      .request("GET", `${videosDownloadVideoUrlApi}/` + id, {
        responseType: "blob",
      })
      .pipe(switchMap((response) => this.readFile(response)));
  }

  private readFile(blob: Blob): Observable<string> {
    console.log(blob);
    return Observable.create((obs) => {
      const reader = new FileReader();
      reader.onerror = (err) => obs.error(err);
      reader.onabort = (err) => obs.error(err);
      reader.onload = () => obs.next(reader.result);
      reader.onloadend = () => obs.complete();
      return reader.readAsDataURL(blob);
    });
  }

  checkVideoById(id: number) {
    return this.http
      .request("GET", `${videosCheckVideoUrlApi}/` + id, {
        responseType: "json",
      })
      .pipe(map((resp: any) => resp));
  }

  checkBookmark(idVideo, idUser) {
    const body = {
      id_video: idVideo,
      id_user: idUser,
      bookmark: true,
    };
    return this.http
      .request("POST", `${videosBookmarksCheckUnCheckListUrlApi}`, { body })
      .pipe(map((resp: any) => resp));
  }

  unCheckBookmark(idVideo, idUser) {
    const body = {
      id_video: idVideo,
      id_user: idUser,
      bookmark: false,
    };
    return this.http
      .request("POST", `${videosBookmarksCheckUnCheckListUrlApi}`, { body })
      .pipe(map((resp: any) => resp));
  }

  blockVideo(idVideo) {
    const body = {
      id_video: idVideo,
      blocked: 1,
    };
    return this.http
      .request("PUT", `${videosBlockUnBlockListUrlApi}`, { body })
      .pipe(map((resp: any) => resp));
  }

  unblockVideo(idVideo) {
    const body = {
      id_video: idVideo,
      blocked: 0,
    };
    return this.http
      .request("PUT", `${videosBlockUnBlockListUrlApi}`, { body })
      .pipe(map((resp: any) => resp));
  }

  getVideoBookmarks() {
    return this.http
      .request("GET", `${videosBookmarksListUrlApi}`, { responseType: "json" })
      .pipe(map((resp: any) => resp));
  }

  updateVideoById(id: number, videoModel: VideoForm) {
    const body = videoModel;
    return this.http
      .request("PUT", `${videosUrlApi}/` + id, { body })
      .pipe(map((resp: any) => resp));
  }

  deleteVideoById(id: number) {
    return this.http
      .request("DELETE", `${videosUrlApi}/` + id, { responseType: "json" })
      .pipe(map((resp: any) => resp));
  }

  getGpsOneVideo(id: number) {
    return this.http
      .request("GET", `${gpsOneVideoUrlApi}/` + id, { responseType: "json" })
      .pipe(map((resp: any) => resp));
  }

  getVideoMetadata(id: number) {
    return this.http
      .request("GET", `${videoMetadataUrlApi}/` + id, { responseType: "json" })
      .pipe(map((resp: any) => resp));
  }
}
